import type { z } from "zod";

import instance from "..";
import { looseArray, looseParse } from "../looseParse";
import {
  achRelationshipSchema,
  bankInstitutionResponseSchema,
  instantAchEligibilitySchema,
  type instantAchTierSchema,
  rapydTransferSchema,
  transferSchema,
} from "./banking.schema";

export type Transfer = z.infer<typeof transferSchema>;
export interface AchTransfer extends Transfer {}

export const getAchTransfers = async (
  accountId: string,
): Promise<Transfer[]> => {
  const res = await instance.get<Transfer[]>(
    `/accounts/${accountId}/transfers`,
  );
  return looseParse(
    looseArray(transferSchema, "getAchTransfers"),
    res.data,
    "getAchTransfers",
  );
};

export const deleteAchTransfer = async (
  accountId: string,
  transferId: string,
): Promise<void> => {
  const res = await instance.delete<void>(
    `/accounts/${accountId}/transfers/${transferId}`,
  );
  return res.data;
};

export interface CreateTransferRequest {
  amount: string;
  direction: string;
  relationship_id?: string;
  bank_id?: string;
  transfer_type: string;
  ira?: {
    tax_year?: string;
    distribution_reason?: string;
    tax_withholding?: {
      fed_pct?: string;
      state_pct?: string;
    };
  };
}

export const createAchTransfer = async (
  accountId: string,
  req: CreateTransferRequest,
): Promise<Transfer> => {
  const res = await instance.post<Transfer>(
    `/accounts/${accountId}/transfers`,
    req,
  );
  return looseParse(transferSchema, res.data, "createAchTransfer");
};

export type RapydTransfer = z.infer<typeof rapydTransferSchema>;

export const getRapydTransfers = async (
  accountId: string,
): Promise<Transfer[]> => {
  const res = await instance.get<RapydTransfer[]>(
    `/rapyd/${accountId}/transfers`,
  );

  const transfers = looseParse(
    looseArray(rapydTransferSchema, "getRapydTransfers"),
    res.data,
    "getRapydTransfers",
  );
  // standardize to fit the abstract transfer object
  transfers.forEach((t) => {
    t.status = t.transfer_status;
    t.type = "rapyd";
    t.direction = "INCOMING";
  });
  return transfers;
};

export type AchRelationshipStatus =
  | "QUEUED"
  | "CANCEL_REQUESTED"
  | "CANCEL_SENT"
  | "CANCEL_FAILED"
  | "PENDING"
  | "SENT_TO_CLEARING"
  | "APPROVED"
  | "CANCELED"
  | "REJECTED";

export type AchRelationship = z.infer<typeof achRelationshipSchema>;

export const getAchRelationships = async (
  accountId: string,
): Promise<AchRelationship[]> => {
  const res = await instance.get<AchRelationship[]>(
    `/accounts/${accountId}/relationships`,
  );
  return looseParse(
    looseArray(achRelationshipSchema, "getAchRelationships"),
    res.data,
    "getAchRelationships",
  );
};

export const deleteAchRelationship = async (
  accountId: string,
  relationshipId: string,
): Promise<void> => {
  const res = await instance.delete<void>(
    `/accounts/${accountId}/relationships/${relationshipId}`,
  );
  return res.data;
};

export type BankInstitutionResponse = z.infer<
  typeof bankInstitutionResponseSchema
>;

export type BankInstitution = BankInstitutionResponse["institution"];

export const getBankInstitution = async (
  institutionId: string,
): Promise<BankInstitution> => {
  const res = await instance.get<BankInstitutionResponse>(
    `/institutions/${institutionId}`,
  );
  return looseParse(
    bankInstitutionResponseSchema,
    res.data,
    "getBankInstitution",
  ).institution;
};

export const getCashWithdrawable = async (accountId: string) => {
  const res = await instance.get<{ cash_withdrawable: number }>(
    `/accounts/${accountId}/trade_account/margin`,
  );
  return Number(res.data.cash_withdrawable ?? 0);
};

export const postFundingSurvey = async (
  accountID: string,
  survey: Record<string, string>,
) => {
  const res = await instance.post(
    `/accounts/${accountID}/survey/funding`,
    survey,
  );
  return res.data;
};

export type InstantAchTier = z.infer<typeof instantAchTierSchema>;

export type InstantAchEligibility = z.infer<typeof instantAchEligibilitySchema>;

export const getInstantAchEligibility = async (
  accountId: string,
): Promise<InstantAchEligibility> => {
  const res = await instance.get<InstantAchEligibility>(
    `/accounts/${accountId}/transfers/instant_ach/eligibility`,
  );
  return looseParse(
    instantAchEligibilitySchema,
    res.data,
    "getInstantAchEligibility",
  );
};
